import { Check, UserRound, Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage, Language } from "@/lib/i18n";

interface EmployeeRegistrationViewProps {
  inputName: string;
  setInputName: (name: string) => void;
  candidateNames: string[];
  handleSaveFirstTimeName: (name: string) => void;
  isSubmitting?: boolean;
  lineDisplayName?: string;
  candidateLoading?: boolean;
}

export function EmployeeRegistrationView({
  inputName,
  setInputName,
  candidateNames,
  handleSaveFirstTimeName,
  isSubmitting = false,
  lineDisplayName,
  candidateLoading = false,
}: EmployeeRegistrationViewProps) {
  const { language, setLanguage } = useLanguage();
  const tx = (th: string, my: string) => (language === "my" ? my : th);

  const handleSelectLanguage = (lang: Language) => {
    setLanguage(lang);
  };

  return (
    <div className="registration-screen flex flex-col justify-center max-w-lg mx-auto py-2 sm:py-6 animate-in fade-in duration-300">
      <Card className="glass-panel rounded-3xl border-amber-200/70 shadow-2xl overflow-hidden relative bg-white/95">
        <div className="h-2.5 bg-gradient-to-r from-[#0b132b] via-[#152238] to-[#f59e0b]" />

        <CardContent className="p-4 sm:p-7 space-y-5">
          {/* Welcome Avatar & Title */}
          <div className="flex items-center gap-3.5 pt-1">
            <div className="flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-800 border-2 border-amber-300/80 shadow-md p-1 overflow-hidden">
              <img
                src="/sirarund-logo.png"
                alt="Sirarund Construction"
                className="h-full w-full object-contain rounded-xl"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <UserRound size={28} className="hidden only:block text-amber-500" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] sm:text-xs font-black text-amber-900 border border-amber-200 mb-1">
                <Sparkles size={11} className="text-amber-600" />
                {tx("เข้าสู่ระบบครั้งแรกผ่าน LINE", "LINE မှ စတင်အသုံးပြုခြင်း")}
              </span>
              <h1 className="text-lg sm:text-2xl font-black text-slate-900 leading-tight">
                {tx("ยินดีต้อนรับสู่ระบบบันทึกเวลา", "အချိန်မှတ်တမ်းစနစ်မှ ကြိုဆိုပါသည်")}
              </h1>
              <p className="text-xs sm:text-sm font-semibold text-slate-600 mt-0.5">
                {tx(
                  "กรุณาเลือกภาษาและระบุชื่อของคุณ เพื่อเข้าสู่ระบบ",
                  "စနစ်သို့ ဝင်ရောက်ရန် ဘာသာစကားနှင့် သင့်အမည်ကို ရွေးချယ်ပါ"
                )}
              </p>
            </div>
          </div>

          {/* LINE Identity connected info */}
          {lineDisplayName && (
            <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-emerald-50/80 border border-emerald-200 text-xs">
              <span className="font-semibold text-emerald-800">
                {tx("บัญชี LINE ที่เชื่อมต่อ:", "ချိတ်ဆက်ထားသော LINE အကောင့်:")}
              </span>
              <span className="font-black text-emerald-900 truncate max-w-[180px]">
                {lineDisplayName}
              </span>
            </div>
          )}

          {/* STEP 1: Prominent Language Selection Cards */}
          <div className="space-y-2 pt-1 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <label className="text-xs sm:text-sm font-black uppercase text-slate-800 flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-slate-950 text-[11px] font-black">
                  1
                </span>
                {tx("เลือกภาษาที่ต้องการใช้งาน", "ဘာသာစကား ရွေးချယ်ပါ")}
                <span className="text-rose-500">*</span>
              </label>
              <span className="text-[11px] font-semibold text-slate-400">
                {tx("แตะเพื่อเปลี่ยน", "နှိပ်၍ ရွေးပါ")}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {/* Thai Card */}
              <button
                type="button"
                onClick={() => handleSelectLanguage("th")}
                className={`flex flex-col items-center justify-center p-3 rounded-2xl border-2 transition-all cursor-pointer text-center relative ${
                  language === "th"
                    ? "border-amber-500 bg-amber-50/90 shadow-md ring-2 ring-amber-500/20 text-slate-950"
                    : "border-slate-200/90 bg-white hover:border-amber-300 text-slate-600"
                }`}
              >
                {language === "th" && (
                  <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-slate-950">
                    <Check size={11} strokeWidth={3} />
                  </span>
                )}
                <span className="text-2xl sm:text-3xl mb-1">🇹🇭</span>
                <span className="text-sm sm:text-base font-black">ภาษาไทย</span>
                <span className="text-[10px] sm:text-xs font-semibold text-slate-500">
                  ไทย (Thai)
                </span>
              </button>

              {/* Myanmar Card */}
              <button
                type="button"
                onClick={() => handleSelectLanguage("my")}
                className={`flex flex-col items-center justify-center p-3 rounded-2xl border-2 transition-all cursor-pointer text-center relative ${
                  language === "my"
                    ? "border-amber-500 bg-amber-50/90 shadow-md ring-2 ring-amber-500/20 text-slate-950"
                    : "border-slate-200/90 bg-white hover:border-amber-300 text-slate-600"
                }`}
              >
                {language === "my" && (
                  <span className="absolute top-2 right-2 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-slate-950">
                    <Check size={11} strokeWidth={3} />
                  </span>
                )}
                <span className="text-2xl sm:text-3xl mb-1">🇲🇲</span>
                <span className="text-sm sm:text-base font-black">မြန်မာစာ</span>
                <span className="text-[10px] sm:text-xs font-semibold text-slate-500">
                  မြန်မာ (Myanmar)
                </span>
              </button>
            </div>
          </div>

          {/* STEP 2: Name Selection / Input */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            <div>
              <label className="text-xs sm:text-sm font-black uppercase text-slate-800 flex items-center gap-1.5 mb-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-slate-950 text-[11px] font-black">
                  2
                </span>
                {tx("ระบุชื่อ-นามสกุลของคุณ", "သင့်အမည်ကို ရွေးချယ်ပါ")}
                <span className="text-rose-500">*</span>
              </label>

              {/* Dropdown from candidate names */}
              <select
                className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3.5 py-3 text-sm sm:text-base font-bold text-slate-900 shadow-xs focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition"
                value={candidateNames.includes(inputName) ? inputName : ""}
                onChange={(e) => {
                  if (e.target.value) setInputName(e.target.value);
                }}
                disabled={isSubmitting}
              >
                <option value="">
                  {candidateLoading
                    ? tx("กำลังโหลดรายชื่อพนักงาน...", "ဝန်ထမ်းစာရင်း ရယူနေသည်...")
                    : tx("-- แตะเพื่อเลือกชื่อของคุณจากรายชื่อ --", "-- ဝန်ထမ်းစာရင်းမှ သင့်အမည်ကို ရွေးပါ --")}
                </option>
                {candidateNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative flex items-center justify-center py-0.5">
              <div className="border-t border-slate-200 w-full" />
              <span className="bg-white px-3 text-[11px] font-bold text-slate-400 uppercase absolute">
                {tx("หรือพิมพ์ชื่อด้วยตนเอง", "သို့မဟုတ် အမည်ကိုယ်တိုင် ရိုက်ထည့်ပါ")}
              </span>
            </div>

            <div>
              <input
                type="text"
                placeholder={tx(
                  "พิมพ์ชื่อ-นามสกุล (เช่น นายสมชาย หรือ Ko Aung)",
                  "အမည်ရိုက်ထည့်ပါ (ဥပမာ- Ko Aung)"
                )}
                className="w-full rounded-2xl border-2 border-slate-200 bg-white px-3.5 py-3 text-sm sm:text-base font-bold text-slate-900 shadow-xs focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none transition"
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {/* STEP 3: Submit / Enter Button */}
            <Button
              className="btn-primary-teal h-14 sm:h-15 w-full rounded-2xl text-base sm:text-lg font-black shadow-lg shadow-amber-500/25 cursor-pointer mt-2"
              onClick={() => handleSaveFirstTimeName(inputName)}
              disabled={!inputName.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={22} />
                  {tx("กำลังเข้าสู่ระบบ...", "စနစ်သို့ ဝင်ရောက်နေသည်...")}
                </>
              ) : (
                <>
                  <Check className="mr-2" size={22} strokeWidth={3} />
                  {tx("เข้าสู่ระบบ", "စနစ်သို့ ဝင်ရောက်မည်")}
                </>
              )}
            </Button>

            {!inputName.trim() && (
              <p className="text-center text-xs font-semibold text-amber-700">
                {tx(
                  "กรุณาเลือกหรือกรอกชื่อพนักงานก่อนเข้าสู่ระบบ",
                  "စနစ်သို့ ဝင်ရောက်ရန် အမည်ရွေးချယ်ပါ သို့မဟုတ် ရိုက်ထည့်ပါ"
                )}
              </p>
            )}

            <div className="flex items-start gap-2 p-3 rounded-2xl bg-slate-50 border border-slate-200 text-[11px] text-slate-600 font-medium leading-relaxed mt-2">
              <ShieldCheck size={17} className="text-amber-600 shrink-0 mt-0.5" />
              <span>
                {tx(
                  "ระบบจะจดจำชื่อและภาษาของคุณ ผูกกับ LINE อัตโนมัติ สแกนครั้งถัดไปจะเปิดหน้าลงเวลาทำงานทันที",
                  "စနစ်သည် သင်၏ LINE နှင့် အမည်ကို အလိုအလျောက် မှတ်သားထားမည်ဖြစ်ပြီး နောက်တစ်ကြိမ်တွင် အချိန်မှတ်တမ်းကို တိုက်ရိုက် ပြသမည်ဖြစ်သည်"
                )}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}